import * as React from 'react'
import { FrameValue, useSpring } from 'react-spring'

/**
 * Can be used to provide the animation progress value from 0 to 1.
 * Used by scroll-based animations.
 */
const AnimationProgressContext = React.createContext<
  FrameValue<number> | undefined
>(undefined)

/**
 * Hook for consuming the animation progress context.
 *
 * @param valueFromProp if specified, uses this. Otherwise uses the value from the context.
 */
export function useAnimationProgress(
  valueFromProp?: AnimationProgressProviderProps['progress']
): FrameValue<number> {
  const fromContext = React.useContext(AnimationProgressContext)
  const [{ defaultValue }] = useSpring<{ defaultValue: number }>(() => ({
    defaultValue: 0,
    immediate: true,
  }))
  return typeof valueFromProp === 'undefined'
    ? typeof fromContext === 'undefined'
      ? defaultValue
      : fromContext
    : valueFromProp
}

/**
 * Props for the context provider.
 */
interface AnimationProgressProviderProps {
  progress: FrameValue<number> | undefined
  children?: React.ReactNode
}

/**
 * Provides the animation progress value.
 * Can be used by intermediate components to forward the one passed through props.
 */
export function AnimationProgressProvider({
  progress,
  children,
}: AnimationProgressProviderProps) {
  if (typeof progress === 'undefined') {
    return <>{children}</>
  }

  return (
    <AnimationProgressContext.Provider value={progress}>
      {children}
    </AnimationProgressContext.Provider>
  )
}
